/**
 * ORCID iDs and the ORCID public API (pub.orcid.org, v3.0).
 *
 * An iD is sixteen characters in four groups, the last a check character computed
 * with ISO 7064 MOD 11-2 over the first fifteen digits — so one mistyped digit, or two
 * swapped, is caught before anything is looked up.
 *
 * A record's `/works` lists one group per work; each group holds that work as every
 * source reported it, preferred version first. Summaries carry the title, type, date,
 * journal and identifiers but no contributors, so works without a DOI are fetched in
 * full (`/works/{put-codes}`, up to 100 at a time) for their contributor list and, when
 * the source supplied one, a BibTeX citation.
 */

import type { EditableEntry } from "@/lib/citation";
import {
  collectMacros,
  fieldText,
  isOthers,
  latexToUnicode,
  parseBibtex,
  parseBibtexName,
  splitBibtexNames,
} from "@/lib/tool-kit/reference-tools/bibtex";

export const ORCID_API = "https://pub.orcid.org/v3.0";
/** The most put-codes ORCID returns in one bulk request. */
export const ORCID_BULK_LIMIT = 100;

const RE_ORCID_PREFIX = /^(?:https?:\/\/)?(?:www\.)?orcid\.org\//i;
const RE_ORCID_LABEL = /^orcid(?:\s*id)?\s*:?\s*/i;
const RE_SEPARATORS = /[\s-]/g;
const RE_ORCID_SHAPE = /^\d{15}[\dX]$/;
const RE_DOI_PREFIX = /^(?:https?:\/\/(?:dx\.)?doi\.org\/|doi:\s*)/i;

/* ------------------------------------------------------------ the iD */

/** ISO 7064 MOD 11-2 over the first fifteen digits: "0" to "9", or "X" for ten. */
export function orcidCheckDigit(base: string): string {
  let total = 0;
  for (const char of base) {
    total = (total + Number(char)) * 2;
  }
  const result = (12 - (total % 11)) % 11;
  return result === 10 ? "X" : String(result);
}

export type OrcidCheck =
  | { ok: true; id: string }
  | { ok: false; reason: "empty" | "format" }
  | { ok: false; reason: "checksum"; expected: string; found: string };

/** Accepts an iD with or without hyphens, as an orcid.org link, or labelled "ORCID:". */
export function checkOrcid(raw: string): OrcidCheck {
  const compact = raw
    .trim()
    .replace(RE_ORCID_PREFIX, "")
    .replace(RE_ORCID_LABEL, "")
    .replace(RE_SEPARATORS, "")
    .toUpperCase();
  if (compact === "") {
    return { ok: false, reason: "empty" };
  }
  if (!RE_ORCID_SHAPE.test(compact)) {
    return { ok: false, reason: "format" };
  }
  const expected = orcidCheckDigit(compact.slice(0, 15));
  const found = compact[15];
  if (expected !== found) {
    return { ok: false, reason: "checksum", expected, found };
  }
  const id = [0, 4, 8, 12].map((at) => compact.slice(at, at + 4)).join("-");
  return { ok: true, id };
}

/* ----------------------------------------------------------- the API */

export function orcidWorksUrl(id: string): string {
  return `${ORCID_API}/${id}/works`;
}

export function orcidPersonUrl(id: string): string {
  return `${ORCID_API}/${id}/person`;
}

export function orcidBulkUrl(id: string, putCodes: readonly number[]): string {
  return `${ORCID_API}/${id}/works/${putCodes.join(",")}`;
}

export const ORCID_HEADERS = { Accept: "application/json" } as const;

export interface OrcidWork {
  putCode: number;
  /** ORCID's work type: journal-article, book-chapter, conference-paper… */
  type: string;
  title: string;
  journal: string;
  year: number | null;
  month: number | null;
  day: number | null;
  doi: string | null;
  isbn: string | null;
  url: string | null;
}

type Json = Record<string, unknown>;

function asRecord(value: unknown): Json | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Json)
    : null;
}

/** ORCID wraps most strings as `{ value: "…" }`. */
function orcidValue(field: unknown): string {
  const value = asRecord(field)?.value;
  return typeof value === "string" ? value.trim() : "";
}

function numberOf(field: unknown): number | null {
  const number = Number.parseInt(orcidValue(field), 10);
  return Number.isFinite(number) ? number : null;
}

interface ExternalId {
  type: string;
  value: string;
  relationship: string;
}

function externalIds(container: unknown): ExternalId[] {
  const items = asRecord(container)?.["external-id"];
  if (!Array.isArray(items)) {
    return [];
  }
  return items.flatMap((raw) => {
    const item = asRecord(raw);
    if (!item) {
      return [];
    }
    const normalized = orcidValue(item["external-id-normalized"]);
    const value =
      normalized ||
      (typeof item["external-id-value"] === "string"
        ? item["external-id-value"].trim()
        : "");
    return [
      {
        type: String(item["external-id-type"] ?? "").toLowerCase(),
        value,
        relationship: String(item["external-id-relationship"] ?? "self"),
      },
    ];
  });
}

function selfId(ids: ExternalId[], type: string): string | null {
  const match =
    ids.find((id) => id.type === type && id.relationship === "self") ??
    ids.find((id) => id.type === type && id.relationship !== "part-of");
  return match?.value ? match.value : null;
}

function workTitle(title: unknown): string {
  const record = asRecord(title);
  const main = orcidValue(record?.title);
  const subtitle = orcidValue(record?.subtitle);
  return subtitle ? `${main}: ${subtitle}` : main;
}

function summaryToWork(raw: unknown): OrcidWork | null {
  const summary = asRecord(raw);
  const putCode = Number(summary?.["put-code"]);
  if (!(summary && Number.isInteger(putCode))) {
    return null;
  }
  const ids = externalIds(summary["external-ids"]);
  const date = asRecord(summary["publication-date"]);
  const doi = selfId(ids, "doi");
  return {
    putCode,
    type: String(summary.type ?? "other"),
    title: workTitle(summary.title),
    journal: orcidValue(summary["journal-title"]),
    year: numberOf(date?.year),
    month: numberOf(date?.month),
    day: numberOf(date?.day),
    doi: doi ? doi.replace(RE_DOI_PREFIX, "").toLowerCase() : null,
    isbn: selfId(ids, "isbn"),
    url: orcidValue(summary.url) || null,
  };
}

/** The works on a record, preferred version of each, newest first. */
export function parseOrcidWorks(body: unknown): OrcidWork[] {
  const groups = asRecord(body)?.group;
  if (!Array.isArray(groups)) {
    return [];
  }
  const works = groups.flatMap((group) => {
    const summaries = asRecord(group)?.["work-summary"];
    const work = Array.isArray(summaries) ? summaryToWork(summaries[0]) : null;
    return work ? [work] : [];
  });
  return sortWorks(works);
}

export function sortWorks(works: readonly OrcidWork[]): OrcidWork[] {
  return [...works].sort(
    (a, b) =>
      (b.year ?? -1) - (a.year ?? -1) ||
      (b.month ?? 0) - (a.month ?? 0) ||
      a.title.localeCompare(b.title)
  );
}

/** The name the record's owner publishes under: credit name, else given + family. */
export function parseOrcidName(body: unknown): string {
  const name = asRecord(asRecord(body)?.name);
  const credit = orcidValue(name?.["credit-name"]);
  if (credit) {
    return credit;
  }
  return [orcidValue(name?.["given-names"]), orcidValue(name?.["family-name"])]
    .filter(Boolean)
    .join(" ");
}

/* ------------------------------------------------- full works (bulk) */

export interface OrcidContributor {
  name: string;
  role: string | null;
}

export interface OrcidDetail {
  putCode: number;
  contributors: OrcidContributor[];
  /** A BibTeX citation the source attached, if any. */
  bibtex: string | null;
  journal: string;
  url: string | null;
}

function contributorsOf(raw: unknown): OrcidContributor[] {
  const list = asRecord(raw)?.contributor;
  if (!Array.isArray(list)) {
    return [];
  }
  return list.flatMap((item) => {
    const record = asRecord(item);
    const name = orcidValue(record?.["credit-name"]);
    if (!name) {
      return [];
    }
    const role = asRecord(record?.["contributor-attributes"])?.[
      "contributor-role"
    ];
    return [
      { name, role: typeof role === "string" ? role.toLowerCase() : null },
    ];
  });
}

/** Full works from a bulk response, keyed by put-code; errors in the bulk are skipped. */
export function parseOrcidBulk(body: unknown): Map<number, OrcidDetail> {
  const bulk = asRecord(body)?.bulk;
  const details = new Map<number, OrcidDetail>();
  if (!Array.isArray(bulk)) {
    return details;
  }
  for (const item of bulk) {
    const work = asRecord(asRecord(item)?.work);
    const putCode = Number(work?.["put-code"]);
    if (!(work && Number.isInteger(putCode))) {
      continue;
    }
    const citation = asRecord(work.citation);
    const isBibtex =
      String(citation?.["citation-type"] ?? "").toLowerCase() === "bibtex";
    const value = citation?.["citation-value"];
    details.set(putCode, {
      putCode,
      contributors: contributorsOf(work.contributors),
      bibtex: isBibtex && typeof value === "string" ? value : null,
      journal: orcidValue(work["journal-title"]),
      url: orcidValue(work.url) || null,
    });
  }
  return details;
}

/* ---------------------------------------------------- work → entry */

/** ORCID work types as CSL types, for the formatter. */
const CSL_TYPES: Record<string, string> = {
  "journal-article": "article-journal",
  "magazine-article": "article-magazine",
  "newspaper-article": "article-newspaper",
  "newsletter-article": "article-magazine",
  book: "book",
  "edited-book": "book",
  manual: "book",
  translation: "book",
  "book-chapter": "chapter",
  "encyclopedia-entry": "entry-encyclopedia",
  "dictionary-entry": "entry-dictionary",
  "conference-paper": "paper-conference",
  "conference-abstract": "paper-conference",
  "conference-poster": "paper-conference",
  "dissertation-thesis": "thesis",
  dissertation: "thesis",
  "supervised-student-publication": "thesis",
  report: "report",
  "working-paper": "report",
  preprint: "article",
  "data-set": "dataset",
  software: "software",
  patent: "patent",
  website: "webpage",
  "online-resource": "webpage",
  "book-review": "review-book",
  review: "review",
  "lecture-speech": "speech",
};

export function cslTypeForOrcid(type: string): string {
  return CSL_TYPES[type] ?? "article";
}

function isoDate(work: OrcidWork): string | undefined {
  if (work.year === null) {
    return undefined;
  }
  const parts = [String(work.year)];
  if (work.month) {
    parts.push(String(work.month).padStart(2, "0"));
    if (work.day) {
      parts.push(String(work.day).padStart(2, "0"));
    }
  }
  return parts.join("-");
}

interface BibFacts {
  authors: string[];
  editors: string[];
  fields: Partial<EditableEntry>;
}

/** "Hirsch, Jorge E." from a parsed BibTeX name: the inverted form the formatter reads. */
function invertedName(raw: string): string {
  const name = parseBibtexName(raw);
  if (name.corporate) {
    return name.last;
  }
  const family = [name.von, name.last].filter(Boolean).join(" ");
  const given = [name.first, name.jr].filter(Boolean).join(" ");
  return given ? `${family}, ${given}` : family;
}

function bibNames(value: string): string[] {
  return splitBibtexNames(value)
    .filter((name) => !isOthers(name))
    .map(invertedName)
    .filter(Boolean);
}

/** What a source-supplied BibTeX citation adds: names, volume, pages, publisher… */
function bibtexFacts(bibtex: string | null): BibFacts | null {
  if (!bibtex) {
    return null;
  }
  const doc = parseBibtex(bibtex);
  const entry = doc.nodes.find((node) => node.kind === "entry");
  if (!entry || entry.kind !== "entry") {
    return null;
  }
  const macros = collectMacros(doc.nodes);
  const get = (name: string) => latexToUnicode(fieldText(entry, name, macros));
  const fields: Partial<EditableEntry> = {
    containerTitle: get("journal") || get("booktitle") || undefined,
    volume: get("volume") || undefined,
    issue: get("number") || undefined,
    page: get("pages") || undefined,
    publisher:
      get("publisher") || get("school") || get("institution") || undefined,
    eventPlace: get("address") || undefined,
    ISBN: get("isbn") || undefined,
    edition: get("edition") || undefined,
  };
  return {
    authors: bibNames(fieldText(entry, "author", macros)),
    editors: bibNames(fieldText(entry, "editor", macros)),
    fields,
  };
}

export interface WorkEntry {
  entry: EditableEntry;
  /** No author list anywhere in ORCID: the record's owner was put in alone. */
  ownerOnly: boolean;
}

function definedFields(fields: Partial<EditableEntry>): Partial<EditableEntry> {
  return Object.fromEntries(
    Object.entries(fields).filter(
      ([, value]) => value !== undefined && value !== ""
    )
  ) as Partial<EditableEntry>;
}

/**
 * A CSL-ready entry built from ORCID alone — for works without a DOI, or whose DOI did
 * not resolve. Names are joined with semicolons, which the formatter splits on, so an
 * inverted "Quinn, J. J." is read as one person.
 */
export function workToEntry(
  work: OrcidWork,
  detail: OrcidDetail | undefined,
  owner: string
): WorkEntry {
  const facts = bibtexFacts(detail?.bibtex ?? null);
  const contributorAuthors = (detail?.contributors ?? [])
    .filter((person) => person.role !== "editor")
    .map((person) => person.name);
  const contributorEditors = (detail?.contributors ?? [])
    .filter((person) => person.role === "editor")
    .map((person) => person.name);
  let authors =
    contributorAuthors.length > 0 ? contributorAuthors : (facts?.authors ?? []);
  const editors =
    contributorEditors.length > 0 ? contributorEditors : (facts?.editors ?? []);
  const ownerOnly =
    authors.length === 0 && editors.length === 0 && owner !== "";
  if (ownerOnly && work.type !== "edited-book") {
    authors = [owner];
  }
  const entry: EditableEntry = {
    type: cslTypeForOrcid(work.type),
    title: work.title || undefined,
    issued: isoDate(work),
    ...definedFields(facts?.fields ?? {}),
    ...definedFields({
      containerTitle: work.journal || detail?.journal || undefined,
      DOI: work.doi ?? undefined,
      ISBN: work.isbn ?? undefined,
      URL: work.url ?? detail?.url ?? undefined,
    }),
  };
  if (authors.length > 0) {
    entry.author = authors.join("; ");
  }
  const editorList =
    ownerOnly && work.type === "edited-book" ? [owner] : editors;
  if (editorList.length > 0) {
    entry.editor = editorList.join("; ");
  }
  return { entry, ownerOnly };
}
